import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import Loader from "../../../../common/Loader";
import useStore from "../../../../../context/useStore";
import EpSessionStatsBar from "./EpSessionStatsBar";
import EpSessionActionsBar from "./EpSessionActionsBar";
import EpDirectorActionsBar from "./EpDirectorActionsBar";
import EpSurseoirModal from "./EpSurseoirModal";
import EpSessionTableV2 from "./EpSessionTableV2";
import EpDecisionModal from "./EpDecisionModal";
import EpSessionFilters from "./EpSessionFilters";
import EpSessionHeader from "./EpSessionHeader";
import EpSessionInfoBar from "./EpSessionInfoBar";
import EpCallingWrapper from "./EpCallingWrapper";
import {
  BulkRuValidateModal,
  BulkDirectorAccordModal,
} from "./EpBulkValidateModal";
import useSessionFilters from "../hooks/useSessionFilters";
import useDirectorDecisions from "../hooks/useDirectorDecisions";
import useBulkValidation from "../hooks/useBulkValidation";
import { EpSessionProvider } from "../EpSessionContext";
import GuidedTour from "./GuidedTour";
import sessionTourSteps from "./tourSteps";
import "./EpSessionMainV2.css";

const EpSessionMainV2 = ({ sessionData }) => {
  const [gState] = useStore();
  const {
    ep,
    inscriptions,
    isLoadingInscriptions,
    stats,
    closeSession,
    isClosing,
    writerPresident,
    setWriterPresident,
    saveWriterPresident,
    isSavingWriters,
    presentJuryMembers,
    fetchInscriptions,
    fetchEp,
    isDirector,
  } = sessionData;

  const [decisionInscription, setDecisionInscription] = useState(null);
  const [showRolesEditor, setShowRolesEditor] = useState(false);
  const [showCalling, setShowCalling] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const scrollToRef = useRef(null);

  const isEnded = !!ep?.endedAt;
  const isClosed = !!ep?.closedAt;
  const showDirectorView = (isDirector || isEnded) && isClosed;

  const iAmPresident = gState.auth.user.id === ep.president;
  const canModifyWriters = [ep.writer, ep.president].includes(
    gState.auth.user.id,
  );

  // --- Hooks métier ---

  const director = useDirectorDecisions(ep, inscriptions, { fetchEp });

  const bulk = useBulkValidation(ep, inscriptions, { fetchInscriptions });

  const { filteredInscriptions, filterProps } = useSessionFilters(
    inscriptions,
    {
      onFilterChange: bulk.clearSelection,
      directorDecisions: showDirectorView
        ? director.directorDecisions
        : undefined,
    },
  );

  // --- Context value (données passives lues par les composants enfants) ---

  const ctxValue = useMemo(
    () => ({
      ep,
      inscriptions,
      isDirector,
      isEnded,
      isClosed,
      showDirectorView,
    }),
    [ep, inscriptions, isDirector, isEnded, isClosed, showDirectorView],
  );

  // --- Scroll après sauvegarde ---

  useEffect(() => {
    if (scrollToRef.current && !isLoadingInscriptions) {
      const id = scrollToRef.current;
      scrollToRef.current = null;
      requestAnimationFrame(() => {
        const el = document.getElementById(`inscription-${id}`);
        setTimeout(() => {
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);
      });
    }
  }, [inscriptions, isLoadingInscriptions]);

  // --- Handlers décision EP ---

  const handleOpenDecision = useCallback((inscription) => {
    setDecisionInscription(inscription);
  }, []);

  const handleCloseDecision = useCallback(() => {
    setDecisionInscription(null);
  }, []);

  const handleDecisionSaved = useCallback(
    (inscriptionId) => {
      setDecisionInscription(null);
      scrollToRef.current = inscriptionId || null;
      fetchInscriptions();
    },
    [fetchInscriptions],
  );

  // --- Clôture président ---

  const handleFinalizeSession = useCallback(() => {
    if (!bulk.canClose) return;
    if (
      window.confirm(
        "Attention : Une fois clôturée, la session sera transmise au directeur pour validation.\n\nConfirmer la clôture ?",
      )
    ) {
      closeSession();
    }
  }, [bulk.canClose, closeSession]);

  // --- Rendu ---

  if (isLoadingInscriptions && inscriptions.length === 0) {
    return (
      <div className="text-center p-5">
        <Loader />
        <p className="mt-3 text-muted">Chargement de la session...</p>
      </div>
    );
  }

  if (showCalling) {
    return (
      <EpCallingWrapper
        sessionData={sessionData}
        onBack={() => setShowCalling(false)}
      />
    );
  }

  return (
    <EpSessionProvider value={ctxValue}>
      <div className="ep-session-main animated fadeInDown faster mb-5 pb-5">
        {/* Header */}
        <EpSessionHeader
          iAmPresident={iAmPresident}
          canClose={bulk.canClose}
          canCloseDefinitively={director.canCloseDefinitively}
          pendingDecisionCount={bulk.pendingDecisionCount}
          pendingDirectorCount={director.pendingDirectorCount}
          directorDecisions={director.directorDecisions}
          isClosing={isClosing}
          isEndingSession={director.isEndingSession}
          onShowCalling={() => setShowCalling(true)}
          onFinalizeSession={handleFinalizeSession}
          onEndSession={director.handleEndSession}
          onStartTour={() => setShowTour(true)}
        />

        {/* EP Info bar + roles editor */}
        <EpSessionInfoBar
          canModifyWriters={canModifyWriters}
          showRolesEditor={showRolesEditor}
          setShowRolesEditor={setShowRolesEditor}
          writerPresident={writerPresident}
          setWriterPresident={setWriterPresident}
          saveWriterPresident={saveWriterPresident}
          isSavingWriters={isSavingWriters}
          presentJuryMembers={presentJuryMembers}
        />

        {/* Stats */}
        <EpSessionStatsBar stats={stats} filterProps={filterProps} />

        {/* Filtres et tri */}
        <EpSessionFilters {...filterProps} />

        {/* Actions en masse */}
        {showDirectorView
          ? !isEnded && (
              <EpDirectorActionsBar
                selectedCount={bulk.selectedIds.length}
                totalCount={filteredInscriptions.length}
                allSelected={
                  filteredInscriptions.length > 0 &&
                  bulk.selectedIds.length === filteredInscriptions.length
                }
                onToggleSelectAll={() =>
                  bulk.handleToggleSelectAll(filteredInscriptions)
                }
                onValidateAllSelected={() =>
                  director.handleDirectorBulkAccord(bulk.selectedIds)
                }
                isBulkSaving={director.isBulkDirectorSaving}
              />
            )
          : !isEnded && (
              <EpSessionActionsBar
                selectedCount={bulk.selectedIds.length}
                totalCount={filteredInscriptions.length}
                allSelected={
                  filteredInscriptions.length > 0 &&
                  bulk.selectedIds.length === filteredInscriptions.length
                }
                onSelectAll={() => bulk.handleSelectAll(filteredInscriptions)}
                onToggleSelectAll={() =>
                  bulk.handleToggleSelectAll(filteredInscriptions)
                }
                onValidateSelected={bulk.handleValidateSelected}
                isClosed={isClosed}
              />
            )}

        {/* Table */}
        <div data-tour="inscriptions-table">
          <EpSessionTableV2
            inscriptions={filteredInscriptions}
            selectedIds={bulk.selectedIds}
            onToggleSelect={bulk.handleToggleSelect}
            onToggleSelectAll={() =>
              bulk.handleToggleSelectAll(filteredInscriptions)
            }
            onOpenDecision={handleOpenDecision}
            isClosed={isClosed}
            isDirector={showDirectorView}
            isReadOnly={isEnded}
            directorDecisions={director.directorDecisions}
            onDirectorAccord={director.handleDirectorAccord}
            onDirectorSurseoir={director.handleDirectorSurseoir}
            isSavingDirector={director.isSavingDirector}
          />
        </div>

        {/* Guided tour */}
        <GuidedTour
          steps={sessionTourSteps}
          isOpen={showTour}
          onClose={() => setShowTour(false)}
        />

        {/* Modale validation en masse RU */}
        {bulk.showBulkModal && (
          <BulkRuValidateModal
            inscriptions={inscriptions}
            selectedIds={bulk.selectedIds}
            overwriteExisting={bulk.overwriteExisting}
            setOverwriteExisting={bulk.setOverwriteExisting}
            isBulkSaving={bulk.isBulkSaving}
            onConfirm={bulk.handleConfirmBulkValidate}
            onCancel={() => bulk.setShowBulkModal(false)}
          />
        )}

        {/* Modale accord en masse directeur */}
        {director.showBulkDirectorModal && (
          <BulkDirectorAccordModal
            inscriptions={inscriptions}
            selectedIds={bulk.selectedIds}
            directorDecisions={director.directorDecisions}
            overwriteDirectorExisting={director.overwriteDirectorExisting}
            setOverwriteDirectorExisting={director.setOverwriteDirectorExisting}
            isBulkDirectorSaving={director.isBulkDirectorSaving}
            onConfirm={() =>
              director.handleConfirmBulkDirectorAccord(
                bulk.selectedIds,
                bulk.clearSelection,
              )
            }
            onCancel={() => director.setShowBulkDirectorModal(false)}
          />
        )}

        {/* Modale surseoir directeur */}
        {director.surseoirInscription && (
          <EpSurseoirModal
            inscription={director.surseoirInscription}
            onConfirm={director.handleConfirmSurseoir}
            onCancel={() => director.setSurseoirInscription(null)}
            isSaving={
              director.isSavingDirector === director.surseoirInscription.id
            }
          />
        )}

        {/* Modal de décision */}
        {decisionInscription && (
          <EpDecisionModal
            inscription={decisionInscription}
            ep={ep}
            onClose={handleCloseDecision}
            onSaved={handleDecisionSaved}
            readOnly={isClosed}
          />
        )}
      </div>
    </EpSessionProvider>
  );
};

export default EpSessionMainV2;
